import Papa from 'papaparse';
import type { Task } from '@/types';
import { handleError } from '@/utils/errorHandler';

/**
 * Format a timestamp to a readable date string.
 *
 * @param timestamp - Timestamp in milliseconds
 * @returns Formatted date string (YYYY-MM-DD HH:mm)
 */
function formatDate(timestamp: number | null): string {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

/**
 * Format time in milliseconds to hours.
 *
 * @param ms - Time in milliseconds
 * @returns Formatted time string in hours
 */
function formatTime(ms: number | null): string {
  if (!ms) return '';
  const hours = Math.round((ms / (1000 * 60 * 60)) * 100) / 100;
  return `${hours}h`;
}

/**
 * Convert a Task object to a flat CSV-friendly structure.
 *
 * @param task - Task object to convert
 * @returns Flat object with CSV-friendly fields
 */
function taskToCsvRow(task: Task, resolveDisplayName?: (id: string) => string): Record<string, string | number> {
  return {
    ID: task.id,
    Nome: task.name,
    Descrizione: task.content,
    Stato: task.status,
    Priorità: task.priority || '',
    Assegnatari: (task.assignees || []).map(a => resolveDisplayName ? resolveDisplayName(a) : a).join(', '),
    Tag: (task.tags || []).join(', '),
    'Data Scadenza': formatDate(task.dueDate),
    'Data Creazione': formatDate(task.dateCreated),
    'Data Inizio': formatDate(task.startDate),
    'Tempo Stimato': formatTime(task.timeEstimated),
    Allegati: (task.attachments || []).length.toString(),
    'Story Points': task.storyPoints?.toString() || '',
    Spazio: task.spaceName || '',
    Lista: task.listName || '',
    'Task Padre': task.parentId || '',
  };
}

/**
 * Export an array of tasks to CSV format and trigger browser download.
 *
 * @param tasks - Array of tasks to export
 */
export function exportTasksToCSV(tasks: Task[], resolveDisplayName?: (id: string) => string): void {
  try {
    // Convert tasks to CSV-friendly format
    const csvData = tasks.map(t => taskToCsvRow(t, resolveDisplayName));

    // Generate CSV string using Papa Parse
    const csv = Papa.unparse(csvData, {
      quotes: true,
      delimiter: ',',
      header: true,
      newline: '\r\n',
    });

    // Create blob with UTF-8 BOM for Excel compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `tasks-export-${timestamp}.csv`;

    // Trigger download
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);
  } catch (error) {
    handleError(error, { level: 'error', message: 'Errore durante l\'esportazione CSV' });
    throw new Error('Impossibile esportare i task in formato CSV');
  }
}
